import { useCallback, useMemo } from 'react';
import { noop } from 'lodash/fp';
import { useDispatch, useSelector, useOnUnmount, useRunOnce } from 'app/lib/hooks';
import { Field, Form, Yup } from 'app/components/core/Form';
import { useDialogContext, DialogChapterLayout } from 'app/components/features/DialogWithChapters';
import {
    doResetSetEnvironment,
    doSetEnvironment,
    selectEnvironments,
    selectSetEnvironmentIsProcessing
} from 'app/store/jobs';
import { selectSnowflakeConnection } from 'app/store/healthcheck';
import { doGetSlackAuthorization, selectIsSlackConfigured } from 'app/store/integrations/slack';
import { selectUserCanManageJobs } from 'app/store/users';
import { selectIsJobManagementEnabled } from 'app/store/applicationConfig';
import { SnowflakeAccountField } from './SnowflakeAccountField.jsx';
import { SlackNotificationsField } from './SlackNotificationsField.jsx';
import { EnvironmentAttributesField } from './EnvironmentAttributesField.jsx';
import { EnvironmentDefaultsField } from './EnvironmentDefaultsField.jsx';

const AccountSchema = Yup.object({
    serviceAccountUid: Yup.string().trim().required()
});

const AttributesSchema = (existingNames, currentName) =>
    Yup.object({
        role: Yup.object({ name: Yup.string().trim() }).required(),
        warehouse: Yup.object({ name: Yup.string().trim() }).required(),
        name: Yup.string()
            .trim()
            .required()
            .test(
                'Duplicate Environment Names',
                'An environment with this name already exists',
                (value) => {
                    const lower = value?.toLowerCase();
                    const names = currentName
                        ? existingNames.filter((n) => n.toLowerCase() !== currentName.toLowerCase())
                        : existingNames;
                    return !names.some((n) => n.toLowerCase() === lower);
                }
            ),
        description: Yup.string().notRequired()
    });

const NotificationsSchema = Yup.object({
    enabled: Yup.boolean().required(),
    slackChannelId: Yup.string().when('enabled', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired()
    })
});

function getSchema(isSlackConfigured, canManageModels, existingNames, currentName) {
    return Yup.object({
        uid: Yup.string().notRequired(),
        account: AccountSchema.label('Snowflake Identity').meta({ summary: true }).required(),
        attributes: AttributesSchema(existingNames, currentName)
            .label('Environment Attributes')
            .meta({ summary: true })
            .required(),
        notifications: NotificationsSchema.label('Slack Notifications')
            .meta({ summary: isSlackConfigured })
            .notRequired(),
        defaults: Yup.object({
            database: Yup.object({ name: Yup.string().trim().notRequired() }).notRequired(),
            schema: Yup.object({ name: Yup.string().trim().notRequired() }).notRequired()
        })
            .label('Environment Defaults')
            .meta({ summary: canManageModels })
            .notRequired()
    }).label('Environment');
}

const getInitialValues = (env = {}) => ({
    uid: env?.uid,
    account: {
        serviceAccountUid: env?.snowflakeServiceAccountUid
    },
    attributes: {
        role: env?.role ? { name: env?.role } : null,
        warehouse: env?.warehouse ? { name: env?.warehouse } : null,
        name: env?.name,
        description: env?.description
    },
    notifications: {
        enabled: Boolean(env?.slackChannelId),
        slackChannelId: env?.slackChannelId
    },
    defaults: {
        database: env?.database ? { name: env?.database, value: env?.database } : null,
        schema: env?.schema ? { name: env?.schema, value: env?.schema } : null
    }
});

export function CreateEnvironmentForm({ children, environment, onSuccess = noop }) {
    const dispatch = useDispatch();
    const snowflakeStatus = useSelector(selectSnowflakeConnection);
    const isAdmin = useSelector(selectUserCanManageJobs);
    const canManageModels = useSelector(selectIsJobManagementEnabled);

    const { onUpdateEnvironment = onSuccess, onUpdateEnvironmentSummary } = useDialogContext();

    useRunOnce(() => {
        dispatch(doGetSlackAuthorization());
    });

    useOnUnmount(() => {
        dispatch(doResetSetEnvironment);
    });

    const isSlackConfigured = useSelector(selectIsSlackConfigured);
    const isProcessing = useSelector(selectSetEnvironmentIsProcessing);
    const environments = useSelector(selectEnvironments);
    const existingNames = useMemo(() => environments.map((e) => e.name), [environments]);
    const schema = useMemo(
        () => getSchema(isSlackConfigured, canManageModels, existingNames, environment?.name),
        [isSlackConfigured, canManageModels, existingNames, environment?.name]
    );

    const fallbackObj = isAdmin ? {} : { role: snowflakeStatus?.role };

    const initialValues = getInitialValues(environment ?? fallbackObj);

    const handleSubmit = useCallback(
        ({ account, attributes, notifications, defaults, ...params }) => {
            params.name = attributes.name;
            params.description = attributes.description;
            params.role = attributes.role?.name;
            params.warehouse = attributes.warehouse?.name;
            params.database = defaults.database ? defaults.database?.value : null;
            params.schema = defaults.schema ? defaults.schema?.value : null;
            params.snowflakeServiceAccountUid = account.serviceAccountUid;

            if (notifications.enabled) {
                params.slackChannelId = notifications.slackChannelId;
            }

            dispatch(doSetEnvironment(params, onUpdateEnvironment));
        },
        [dispatch, onUpdateEnvironment]
    );

    return (
        <Form
            schema={schema}
            initialValues={initialValues}
            isSubmitting={isProcessing}
            onUpdateSummary={onUpdateEnvironmentSummary}
            onSubmit={handleSubmit}>
            <DialogChapterLayout id="environment-form">
                <SnowflakeAccountField />
                <EnvironmentAttributesField />
                {isSlackConfigured && <SlackNotificationsField />}
                {canManageModels && <EnvironmentDefaultsField />}
                <Field component="input" type="hidden" name="uid" />
            </DialogChapterLayout>
            {children}
        </Form>
    );
}
